import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { Vehicle } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 車両削除API
 */
export const useDeleteVehicleAPI = (): {
  state: APIState;
  deleteVehicle: (vehicle: Vehicle) => Promise<string | null>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const deleteVehicleAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.delete(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}`,
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  /**
   * @return {String} 削除成功した場合 vehicle_id を返す
   */
  const deleteVehicle = useAtomCallback(
    useCallback(
      async (get, _, vehicle: Vehicle) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) && isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.delete_vehicle', {
              status: 'failed',
              name: vehicle.vehicle_name,
            }),
          });
          setState('hasError');
          return null;
        }
        setState('loading');
        const res = await deleteVehicleAPI(
          project!.id,
          environment!.environment_id,
          vehicle.vehicle_id,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.delete_vehicle', {
              status: 'failed',
              name: vehicle.vehicle_name,
            })}: ${getErrorMessage(res)}`,
          });
          return null;
        }
        notifySuccess({
          message: t('api.fms.delete_vehicle', {
            status: 'successfully',
            name: vehicle.vehicle_name,
          }),
          testIdAttrs: {
            wrapperTestId: 'vehicle_delete_snackbar_success-status-div',
            closeButtonTestId:
              'vehicle_delete_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return vehicle.vehicle_id;
      },
      [notifyError, notifySuccess, deleteVehicleAPI, t, getErrorMessage],
    ),
  );

  return {
    state,
    deleteVehicle,
  };
};
